/**
 * @fileoverview marketing-shell's English strings — the words the SHELL itself renders on every Colony
 * marketing site and on the tenant site: the skip link, the mobile menu button, the product and
 * language switchers, the cookie banner and the footer's copyright line.
 * @package @puffin-core/marketing-shell
 *
 * Placeholders use single braces (`{year}`), the namespace translator's convention.
 * The cookie banner's words are the owner's ruling (LIFE-SHORTLISTED-001 F-Q7-1, 2026-09-28).
 *
 * @module @puffin-core/marketing-shell/strings/en
 */

export const MARKETING_SHELL_EN = Object.freeze({
  skipLink: 'Skip to main content',
  menu: Object.freeze({
    open: 'Open menu',
    close: 'Close menu',
  }),
  ecosystemSwitcher: Object.freeze({
    label: 'Products',
  }),
  localeSwitcher: Object.freeze({
    label: 'Language',
  }),
  cookieConsent: Object.freeze({
    title: 'Cookie Preferences',
    message: 'We use cookies to improve your experience and analyze site traffic. See our',
    privacyLink: 'Privacy Policy',
    moreInfo: 'for more information.',
    decline: 'Decline',
    accept: 'Accept',
    settings: 'Cookie settings',
    gpcNotice:
      'Your browser sends a Global Privacy Control signal, so analytics are off unless you accept here.',
  }),
  footer: Object.freeze({
    connect: 'Connect',
    copyright: '© {year} {owner}. All rights reserved.',
    opensInNewTab: '(opens in a new tab)',
    // PACKAGE-I18N-SEAT (JSDOC-PROP-CONTRACT-001, push 17): the footer landmark's name when the
    // caller gives none.
    siteFooter: '{appName} site footer',
    // `<MarketingFooter>`'s words when the caller gives none (the default-parameter arm of
    // `no-hardcoded-strings`): the two legal links and the contact column's heading.
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    contact: 'Contact',
  }),
  // The default-parameter arm of `no-hardcoded-strings` (JSDOC-PROP-CONTRACT-001): words a host may
  // leave out, each an English default parameter until then.
  readingProgress: Object.freeze({
    label: 'Reading progress',
  }),
  relatedPosts: Object.freeze({
    heading: 'Related posts',
  }),
  tableOfContents: Object.freeze({
    label: 'Table of contents',
  }),
  demoRequestModal: Object.freeze({
    close: 'Close',
  }),
  // PACKAGE-I18N-SEAT — words four shared components wrote in English.
  blogPagination: Object.freeze({
    label: 'Blog pagination',
    previous: 'Previous',
    next: 'Next',
    pageOf: 'Page {page} of {total}',
    page: 'Page {page}',
  }),
  founderProfile: Object.freeze({
    signature: 'Signature: {name}',
  }),
  testimonials: Object.freeze({
    previous: 'Previous testimonial',
    next: 'Next testimonial',
    slides: 'Testimonial slides',
    goTo: 'Go to testimonial {position} of {count}',
  }),
  contactForm: Object.freeze({
    // The honeypot's label: hidden from people, read by the bots it exists to catch.
    honeypot: 'Website (leave this empty)',
    // `<ContactForm>`'s words when the caller gives none: the submit button (idle and submitting)
    // and the form's accessible name; `<ContactFormSuccess>`'s reference label.
    submit: 'Submit',
    submitting: 'Submitting…',
    formLabel: 'Contact form',
    referenceId: 'Reference ID',
  }),
  // LIFE-SHORTLISTED-001 Q-LS-32 — the tenant SITE's chrome words (its header nav fallback and its
  // footer), here because the tenant site, its stored pages and its server-rendered blog render ONE
  // chrome (storefront-cms `<TenantSiteChrome>`, PROTECTOR-SMS-001 U1b) in the tenant's declared language. Moved from purchase-web's
  // `storefront.nav.*` / `storefront.footer.*`, whose only readers were that chrome.
  // APP-ENTITLEMENT-001 Phase 11 — the pricing section every Colony marketing site renders from the ONE app
  // catalog (`<AppPricingSection>`): the anchors, the apps beside them, the free apps, the processing rate, the
  // three-store worked example and the billing-cycle toggle. The catalog's own words (a unit, a consumable, an
  // included feature, "free with your anchor") are `accountAppsUi`'s, read through its `/copy` leaf.
  pricing: Object.freeze({
    anchorsHeading: 'Start with an anchor',
    anchorsSubheading:
      'Every account starts with one. Add any app beside it, any day; changes are prorated both ways.',
    includesHeading: '{anchor} includes',
    freeAppFeature: '{app}, free',
    perUnitMonth: 'per {unit} a month',
    perUnitYear: 'per {unit} a year',
    flatMonth: 'a month',
    flatYear: 'a year',
    annualNote: '{monthly} a month, billed annually — {free} months free',
    comingSoon: 'Coming soon',
    startTrial: 'Start your free trial',
    startTrialOf: 'Start a free trial of {app}',
    trialTerms:
      '{days}-day free trial on every app, no card. When a trial ends without a card the app goes read-only — nothing is deleted.',
    addOnsHeading: 'Add what you need',
    addOnsSubheading: 'Each app is priced on its own and starts with its own trial.',
    addOnsCaption: 'Apps you can add beside {anchor}',
    columnApp: 'App',
    columnPrice: 'Price',
    columnTrial: 'Trial',
    columnAction: 'Try it',
    trialDays: '{days} days, no card',
    notYetOrderable: 'Not yet available to order',
    freeAppsHeading: 'Free with every anchor',
    freeAppsBody: 'These come with any anchor, at no charge.',
    cycleAria: 'Billing cycle',
    cycleMonthly: 'Monthly',
    cycleAnnual: 'Annual — {free} months free',
    processingRate:
      'Card payments: {rate}% + {fixed} per transaction, published and the same for everyone.',
    exampleHeading: 'What three stores pay',
    exampleCaption:
      '{anchor} at three locations, one of them with four registers, with {apps} — before tax.',
    exampleCaptionAnchorOnly:
      '{anchor} at three locations, one of them with four registers — before tax.',
    columnLine: 'What',
    columnMonthly: 'Monthly',
    columnAnnual: 'Annual',
    annualColumnNote: 'Annual is exactly {months} months’ price for twelve.',
    totalRow: 'Total',
    quoteLine: '{quantity} {units} · {amount} a month',
    totalMonthly: '{amount} a month',
    totalAnnual: '{amount} a year ({monthly} a month)',
    loading: 'Loading prices…',
    loadFailed: 'We couldn’t load our prices. Please try again.',
    retry: 'Retry',
    // The words a pricing page's Product JSON-LD carries (a crawler reads these, never a person on the page).
    jsonLdUnitText: 'per {unit} a month',
    jsonLdProductDescription:
      "{app}, from the ParcelPuffin Colony's app catalog. Every app starts with a free trial.",
  }),
  tenantSite: Object.freeze({
    nav: Object.freeze({
      label: 'Main',
      home: 'Home',
      blog: 'Blog',
      products: 'Products',
      customDesign: 'Custom Design',
      cart: 'Cart',
      // PROTECTOR-SMS-001 U1b — the chrome's signed-out account link: what a stored document and the app's first
      // render both draw for a site with customer accounts (`<TenantSiteSignInLink>`).
      signIn: 'Sign in',
    }),
    // LIFE-SHORTLISTED-001 P6 — the click-to-load video facade.
    video: Object.freeze({
      play: 'Play video: {title}',
      notice: 'Playing it loads the {provider} player, which may set its own cookies.',
    }),
    // The words a tenant site's visitor reads from storefront-cms's page and blocks (the P6.6b census).
    page: Object.freeze({
      loading: 'Loading…',
      loadFailed: 'This page failed to load.',
      retry: 'Try again',
      notFoundTitle: 'Page not found',
      notFoundBody: 'The page you are looking for does not exist.',
    }),
    preview: Object.freeze({
      notice: 'Preview — this page includes unpublished changes and is not visible to the public.',
      linkInvalidTitle: 'This preview link has expired or is not valid',
      linkInvalidBody: 'Ask the page’s author for a new preview link.',
    }),
    product: Object.freeze({
      loadingOne: 'Loading product…',
      loadingMany: 'Loading products…',
      view: 'View product',
    }),
    // DTOWNRETAIL-SITE-001 U2 — the words a store page and the locations block read: a store's
    // actions and the labels of its facts. The facts themselves (hours, address) are data.
    locations: Object.freeze({
      visit: 'Visit {name}',
      directions: 'Get directions',
      reviews: 'Read reviews',
      writeReview: 'Write a review',
      call: 'Call',
      email: 'Email',
      address: 'Address',
      hours: 'Hours',
      services: 'Services',
      contactCta: 'Contact us',
      notFoundTitle: 'Store not found',
      notFoundBody: 'This store is not listed, or it has closed.',
      newTab: 'opens in a new tab',
    }),
    // DTOWNRETAIL-SITE-001 V1 — the package-tracking lookup block.
    tracking: Object.freeze({
      numberLabel: 'Tracking number',
      numberHint: 'As printed on your receipt or label — letters, digits and spaces.',
      submit: 'Track package',
      submitting: 'Looking up…',
      resultHeading: 'Package {number}',
      status: 'Status',
      carrier: 'Carrier',
      parcel: 'Parcel {sequence} of {count}',
      shipped: 'Shipped',
      delivered: 'Delivered',
      lastUpdated: 'Last updated',
      estimatedDelivery: 'Estimated delivery',
      progress: 'Progress',
      noProgress: 'No scans have been recorded yet.',
      // V1-b: where the standing came from.
      source: Object.freeze({
        carrier: 'Updated from the carrier’s scans; refreshed every 30 minutes.',
        records: 'From our shipping records.',
      }),
      // V1-b: the carrier's own scan words (`CARRIER_SCAN_STATUS`).
      scans: Object.freeze({
        unknown: 'Carrier update',
        pre_transit: 'Label created',
        in_transit: 'In transit',
        out_for_delivery: 'Out for delivery',
        available_for_pickup: 'Available for pickup',
        delivered: 'Delivered',
        return_to_sender: 'Returning to sender',
        failure: 'Delivery attempt failed',
        cancelled: 'Cancelled',
        error: 'The carrier reported a problem',
      }),
      carrierPage: 'Track on the carrier’s site',
      newTab: 'opens in a new tab',
      again: 'Track another package',
      errors: Object.freeze({
        invalid: 'Enter the tracking number as printed: 6 to 40 letters, digits and spaces.',
        notFound: 'No shipment matches that tracking number. Check the number and try again.',
        rateLimited: 'Too many lookups. Please wait a moment and try again.',
        captchaFailed: 'CAPTCHA verification failed — please try again.',
        network: 'Network problem — check your connection and try again.',
        unknown: 'We could not look that up right now. Please try again.',
      }),
      statuses: Object.freeze({
        pending: 'Received',
        processing: 'Being prepared',
        awaiting_pickup: 'Awaiting carrier pickup',
        shipped: 'Shipped',
        in_transit: 'In transit',
        delivered: 'Delivered',
        returned: 'Returned to sender',
        cancelled: 'Cancelled',
        imported_unknown: 'Status unavailable',
      }),
    }),
    // DTOWNRETAIL-SITE-001 V2 — the shipping-estimate block, on the counter quote engine (2026-10-14).
    shipping: Object.freeze({
      fromStore: 'Ship from',
      fromStoreAny: 'Choose a store',
      toPostal: 'To ZIP code',
      postalHint: 'A US ZIP code, 5 digits (ZIP+4 is fine).',
      weight: 'Weight',
      weightUnit: 'Weight unit',
      dimensions: 'Size',
      length: 'Length',
      width: 'Width',
      height: 'Height',
      dimensionUnit: 'Size unit',
      addressClass: 'Delivering to',
      submit: 'Estimate shipping',
      submitting: 'Pricing…',
      resultHeading: 'Estimated rates',
      disclaimer:
        'These are estimates. Your final price is set at the counter, where we weigh and measure your parcel.',
      noQuotes: 'No service quoted this parcel. Bring it in and we will price it at the counter.',
      notPriced: 'Services we could not price for this parcel: {count}.',
      perQuote: '{carrier} {service}',
      billedWeight: 'Billed at {pounds} lb',
      again: 'Estimate another parcel',
      units: Object.freeze({
        oz: 'oz',
        lb: 'lb',
        g: 'g',
        kg: 'kg',
        in: 'in',
        cm: 'cm',
      }),
      addressClasses: Object.freeze({
        residential: 'A home',
        commercial: 'A business',
      }),
      errors: Object.freeze({
        store: 'Choose the store you are shipping from.',
        toPostal: 'Enter the ZIP code you are shipping to.',
        weight: 'Enter a weight greater than zero.',
        dimensions: 'Enter all three sides, each greater than zero.',
        outOfRange:
          'That parcel is beyond what a carrier accepts (150 lb; 108 in on a side). Bring it in and we will help.',
        storeUnknown: 'That store is not available. Choose another.',
        refused:
          'No service at that store can take this parcel to that address. Bring it in and we will help.',
        unavailable:
          'We cannot price shipping right now. Please try again, or bring your parcel in.',
        rateLimited: 'Too many estimates. Please wait a moment and try again.',
        captchaFailed: 'CAPTCHA verification failed — please try again.',
        network: 'Network problem — check your connection and try again.',
        unknown: 'We could not price that right now. Please try again.',
      }),
    }),
    contact: Object.freeze({
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      message: 'Message',
      submit: 'Send message',
    }),
    // DTOWNRETAIL-SITE-001 V4 — the newsletter sign-up form and its one answer.
    newsletter: Object.freeze({
      heading: 'News from the shoppe',
      emailLabel: 'Email address',
      consentText:
        'Email me news and offers from this store. I can unsubscribe at any time — every email has an unsubscribe link.',
      submit: 'Sign up',
      submitting: 'Signing up…',
      sentHeading: 'Check your inbox',
      sentBody:
        'If that address is new to us, we have sent a confirmation link. Nothing is sent until you open it.',
      again: 'Sign up another address',
      errors: Object.freeze({
        invalid: 'Enter a valid email address.',
        consentRequired: 'Tick the box to agree to receive email from this store.',
        rateLimited: 'Too many sign-ups. Please wait a moment and try again.',
        captchaFailed: 'CAPTCHA verification failed — please try again.',
        unavailable: 'Sign-ups are not available on this site right now.',
        deliveryUnavailable:
          'We could not send the confirmation email right now. Please try again in a few minutes.',
        network: 'Network problem — check your connection and try again.',
        unknown: 'We could not sign you up right now. Please try again.',
      }),
    }),
    // DTOWNRETAIL-SITE-001 V5 — the price list: "from" is the lowest price across the open shoppes.
    priceList: Object.freeze({
      from: 'From {price}',
      at: 'Prices at {store}',
      chainNote:
        '“From” is the lowest price across our open shoppes; each shoppe’s page shows its own price.',
    }),
    footer: Object.freeze({
      label: 'Site footer',
      privacy: 'Privacy',
      terms: 'Terms',
      disclosurePage: 'Disclosure',
      accessibility: 'Accessibility',
      privacyRequest: 'Privacy request',
      explore: 'Explore',
      connect: 'Connect',
      // The platform credit's prefix (LINKABLE-ASSETS-001 E1, owner ruling R-65): "Powered by" as
      // text, the platform's brand NAME as the one anchor beside it.
      poweredBy: 'Powered by',
    }),
  }),
});
